import './insuranceFormComponent.scss'
import React, {useCallback, useEffect, useRef, useState} from 'react';
import _ from "lodash";
import {CommonService} from "../../services";
import {IAPIResponseType} from "../../models/api.model";
import {Field, FieldProps, Form, Formik, FormikHelpers, FormikProps, FormikValues} from "formik";
import Grid from "@mui/material/Grid";
import ErrorComponent from "../form-controls/error/errorComponent";
import ButtonComponent from '../core/button/ButtonComponent';
import DialogComponent from "../dialog/dialogComponent";
import FilePreviewerComponent from "../file-previewer/filePreviewerComponent";
import FormikAutoCompleteDropdown from '../core/formik-auto-complete-dropdown/formikAutoCompleteDropdown';
import FormikInputComponent from '../core/formik-input/FormikInputComponent';
import * as Yup from "yup";
import HeadingComponent from '../heading/headingComponent';
import ChipComponent from "../chip/ChipComponent";
import {AttachFile} from '@mui/icons-material';
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import TooltipComponent from "../tooltip/tooltipComponent";
import HelpIcon from "@mui/icons-material/Help";
import FormikRadioButtonGroupComponent from "../core/formik-radio-button-group/formikRadioButtonGroupComponent";
// import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FormikDatePickerComponent from "../core/formik-date-picker/FormikDatePickerComponent";
import formValidationRules from "../../services/validation.service";
// import CancelIcon from '@mui/icons-material/Cancel';
import commonService from "../../services/common.service";
import PatientEditConfirmationComponent
    from "../../../screens/patient/patient-details/patient-edit-confirmation/PatientEditConfirmationComponent";
import FormikSelectDropdownComponent from "../core/formik-select-dropdown/FormikSelectDropdownComponent";

export interface InsuranceFormComponentProps {
    formInitialValues?: any;
    onUpdateComplete?: Function;
    patientID?: any;
    formTitle?: any;
    insuranceMode?: any;
    isInsuranceImageRequired?: boolean;
}

const whenOtherInsuranceRequired = (message: string) =>
    Yup.string().nullable().when('insuranceCompany', {
        is: (insuranceCompany: any) => insuranceCompany?.code?.toLowerCase() === "other",
        then: Yup.string().required(message),
    });
const insuranceValidationSchema = (imageRequired: boolean | undefined) => {
    return Yup.object({
        reasonForEdit: Yup.string(),
        isSameName: Yup.boolean(),
        firstName: Yup.string().when('isSameName', {
            is: (isSameName: boolean) => isSameName === false,
            then: Yup.string().required('First Name is required.'),
        }),
        middleName: Yup.string().nullable(),
        lastName: Yup.string().when('isSameName', {
            is: (isSameName: boolean) => isSameName === false,
            then: Yup.string().required('Last Name is required.'),
        }),
        dateOfBirth: Yup.date().when('isSameName', {
            is: (isSameName: boolean) => isSameName === false,
            then: Yup.date()
                .nullable()
                .typeError("Please Enter valid Date")
                .min(new Date(1900, 0, 1), "Date of birth should be greater than 01/01/1900")
                .max(new Date(), "Can not be in the future")
                .required('Date of Birth is required.'),
        }),
        insuranceCompany: Yup.mixed().required('Insurance Company is required.'),
        newInsuranceCompany: whenOtherInsuranceRequired('Insurance Company is required'),
        relationship: Yup.mixed().required('Relationship is required.'),
        policyNumber: Yup.string()
            .required('Policy Number is required.')
            .max(15, 'Must be 15 characters or less'),
        // isVerified: Yup.boolean(),
        isChecked: Yup.boolean(),
        frontImage: imageRequired ? Yup.mixed().required() : Yup.mixed(),
        backImage: imageRequired ? Yup.mixed().required() : Yup.mixed(),
        networkPlanName: Yup.string().nullable().required("Required"),
        groupName: Yup.string().nullable(),
        groupNetwork: Yup.string().nullable(),
        effectiveDate: Yup.date()
            .nullable()
            .typeError("Please Enter valid Date"),
        // .min(new Date(Date.now()), "Date should not be less than current date"),
        ipaMedicalGroupName: Yup.string().nullable(),
        //field added as per new clearing house changes
        clearingHouse: whenOtherInsuranceRequired('Clearing House is required'),
        clearingHouseId: Yup.string().nullable(),
        CPID: whenOtherInsuranceRequired('CPID is required'),
        groupId: Yup.string().nullable(),
        type: whenOtherInsuranceRequired('Insurance Type is required'),
    })
};

interface insuranceDetailsFormSchema {
    reasonForEdit: string;
    isSameName: boolean,
    firstName: string,
    middleName: string,
    lastName: string,
    dateOfBirth: string,
    isChecked: boolean,
    insuranceCompany: any,
    insurancePlan: any,
    policyNumber: string,
    relationship: any,
    frontImage: any,
    backImage: any,
    networkPlanName: string | null,
    groupName: string | null,
    groupNetwork: string | null,
    effectiveDate: string | null,
    ipaMedicalGroupName: string | null,
    //field added as per new clearing house changes
    clearingHouse: null | string,
    clearingHouseId: null | string,
    CPID: null | string,
    groupId: null | string,
    type: null | string,
}

const insuranceDetailsFormInitialValues: insuranceDetailsFormSchema = {
    reasonForEdit: '',
    isSameName: false,
    isChecked: false,
    firstName: '',
    middleName: '',
    lastName: '',
    dateOfBirth: '',
    insuranceCompany: null,
    insurancePlan: null,
    policyNumber: '',
    relationship: null,
    frontImage: null,
    backImage: null,
    networkPlanName: null,
    groupName: null,
    groupNetwork: null,
    effectiveDate: null,
    ipaMedicalGroupName: null,
    //field added as per new clearing house changes
    clearingHouse: '',
    clearingHouseId: '',
    CPID: '',
    groupId: '',
    type: '',
}

const InsuranceFormComponent = (props: InsuranceFormComponentProps) => {

    const {patientID, onUpdateComplete, insuranceMode, formInitialValues, isInsuranceImageRequired} = props;
    const [insuranceInitialValues, setInsuranceInitialValues] = useState<insuranceDetailsFormSchema>(insuranceDetailsFormInitialValues);
    const {insuranceCompanyList, relationShipList} = useSelector((state: IRootReducerState) => state.staticData);

    const [patientDetails, setPatientDetails] = useState<any>(null);
    const insuranceFrontFileRef = useRef<HTMLInputElement | null>(null);
    const insuranceBackFileRef = useRef<HTMLInputElement | null>(null);
    const [showFrontAttachmentPreviewDialog, setShowFrontAttachmentPreviewDialog] = useState<boolean>(false);
    const [showBackAttachmentPreviewDialog, setShowBackAttachmentPreviewDialog] = useState<boolean>(false);
    const [currentBackAttachmentField, setCurrentBackAttachmentField] = useState<string | undefined>(undefined);
    const [currentBackAttachmentFile, setCurrentBackAttachmentFile] = useState<File | undefined>(undefined);
    const [currentFrontAttachmentField, setCurrentFrontAttachmentField] = useState<string | undefined>(undefined);
    const [currentFrontAttachmentFile, setCurrentFrontAttachmentFile] = useState<File | undefined>(undefined);
    const [checked] = useState<boolean>(true);// enbbot verified not required
    const formikRef = useRef<FormikProps<FormikValues>>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    // const [verified, setVerified] = useState<boolean>(false);
    // const [isInsuranceVerifying, setIsInsuranceVerifying] = useState<boolean>(false);
    const [isNewBackInsuranceImage, setNewBackInsuranceImage] = useState<boolean>(false);
    const [isNewFrontInsuranceImage, setNewFrontInsuranceImage] = useState<boolean>(false)
    const [isFrontCancelVisible, setIsFrontCancelVisible] = useState<boolean>(false);
    const [isBackCancelVisible, setIsBackCancelVisible] = useState<boolean>(false);
    // const [enbotDetails, setEnbotDetails] = useState<any>(null);
    const [enbotDetails] = useState({
        status: false,
        message: '',
        enb_check: 'failed',
        enb_error_message: 'default',
        isVerified: false,
        isServiceAvailable: true
    })
    // const [enbotError, setEnbotError] = useState<boolean>(false);
    // const [enbotErrorMessage, setEnbotErrorMessage] = useState<string>('');

    const handleInsuranceAttachmentUpload = useCallback((fieldName: any, event: any) => {
        const file = event?.target?.files[0];
        const maxAllowedSize = 5 * 1024 * 1024;
        if (event?.target?.files[0].size > maxAllowedSize) {
            CommonService._alert.showToast('File size should be under 5MB.', "error");
            event.target.value = null;
            event.target.files = null;
            return;
        }
        event.target.value = null;
        event.target.files = null;

        if (fieldName === 'frontImage') {
            setIsFrontCancelVisible(false);
            setShowFrontAttachmentPreviewDialog(true);
            setCurrentFrontAttachmentField(fieldName);
            setCurrentFrontAttachmentFile(file);
            setNewFrontInsuranceImage(true);
        } else {
            setIsBackCancelVisible(false);
            setShowBackAttachmentPreviewDialog(true);
            setCurrentBackAttachmentField(fieldName);
            setCurrentBackAttachmentFile(file);
            setNewBackInsuranceImage(true);
        }
    }, []);

    const handleAttachmentPreviewOpen = useCallback((fieldName: any) => {
        if (fieldName === 'frontImage') {
            setIsFrontCancelVisible(true);
            setShowFrontAttachmentPreviewDialog(true);
            setCurrentFrontAttachmentField(fieldName);
            setNewFrontInsuranceImage(false);
            if (formikRef.current) {
                setCurrentFrontAttachmentFile(_.get(formikRef.current.values, fieldName).attachmentUrl);
            }
        } else {
            setIsBackCancelVisible(true);
            setShowBackAttachmentPreviewDialog(true);
            setCurrentBackAttachmentField(fieldName);
            setNewBackInsuranceImage(false);
            if (formikRef.current) {
                setCurrentBackAttachmentFile(_.get(formikRef.current.values, fieldName).attachmentUrl);
            }
        }
    }, [formikRef]);

    const handleFrontAttachmentDialogClose = useCallback(() => {
        setShowFrontAttachmentPreviewDialog(false);
        setIsFrontCancelVisible(false);
    }, []);

    const handleBackAttachmentDialogClose = useCallback(() => {
        setShowBackAttachmentPreviewDialog(false);
        setIsBackCancelVisible(false);
    }, []);

    // const handleClearAll = useCallback(() => {
    //     if (formikRef.current) {
    //         formikRef.current.setFieldValue("insuranceCompany", null);
    //         formikRef.current.setFieldValue("newInsuranceCompany", null);
    //         formikRef.current.setFieldValue("relationship", null);
    //         formikRef.current.setFieldValue("policyNumber", "");
    //         formikRef.current.setFieldValue("frontImage", null);
    //         formikRef.current.setFieldValue("backImage", null);
    //     }
    // }, []);

    const handleFrontAttachmentDialogContinue = useCallback(() => {
        if (formikRef.current && currentFrontAttachmentField) {
            formikRef.current.setFieldValue(currentFrontAttachmentField, currentFrontAttachmentFile);
        }
        setShowFrontAttachmentPreviewDialog(false);
    }, [formikRef, currentFrontAttachmentField, currentFrontAttachmentFile]);

    const handleBackAttachmentDialogContinue = useCallback(() => {
        if (formikRef.current && currentBackAttachmentField) {
            formikRef.current.setFieldValue(currentBackAttachmentField, currentBackAttachmentFile);
        }
        setShowBackAttachmentPreviewDialog(false);
    }, [formikRef, currentBackAttachmentField, currentBackAttachmentFile]);

    const getPatientDetails = useCallback((patientID: any) => {
        const payload = {};
        CommonService._patient.getPatientDetails(patientID, payload)
            .then((response: IAPIResponseType<any>) => {
                if (response?.data) {
                    setPatientDetails(response.data);
                }
            })
            .catch((error: any) => {

            });
    }, []);

    useEffect(() => {
        if (patientID) {
            getPatientDetails(patientID);
        }
    }, [patientID, getPatientDetails])

    // const verifyInsuranceDetails = useCallback((values: any, setFieldValue: any) => {
    //     setIsInsuranceVerifying(true);
    //     const payload = {...values};
    //     if (payload?.isSameName) {
    //         payload.firstName = patientDetails?.firstName;
    //         payload.dateOfBirth = patientDetails?.dateOfBirth;
    //         payload.middleName = patientDetails?.middleName;
    //         payload.lastName = patientDetails?.lastName;
    //     }
    //     if (payload?.insuranceCompany) {
    //         payload.payerId = payload?.insuranceCompany?.payerId;
    //         payload.insuranceCompany = payload?.insuranceCompany?.code;
    //     }
    //     if (payload?.insurancePlan) {
    //         payload.insurancePlan = payload?.insurancePlan?.code;
    //     }
    //     if (payload?.relationship) {
    //         payload.relationship = payload?.relationship?.code;
    //     }
    //     CommonService._patient.verifyPatientInsurance(patientID, payload)
    //         .then((response: IAPIResponseType<any>) => {
    //             setChecked(true);
    //             setEnbotDetails(response?.data);
    //             setFieldValue('isChecked', true);
    //             if (response?.data?.isVerified) {
    //                 setVerified(true);
    //                 setFieldValue("isVerified", true);
    //                 setEnbotError(false);
    //                 setEnbotErrorMessage('');
    //
    //                 CommonService._alert.showToast(response?.message || 'Insurance details has been added', "success");
    //             }
    //             if (!response?.data?.isVerified) {
    //                 setVerified(true);
    //                 setFieldValue("isVerified", true);
    //             }
    //             setIsInsuranceVerifying(false);
    //         })
    //         .catch((error: any) => {
    //             setFieldValue('isChecked', true);
    //             setEnbotError(true);
    //             setEnbotErrorMessage(error?.data?.enb_error_message || 'Something went wrong');
    //             setEnbotDetails(error?.data);
    //             setChecked(true);
    //             setIsInsuranceVerifying(false);
    //         })
    // }, [patientID, patientDetails])

    const handleDialogOpen = useCallback(() => {
        setIsDialogOpen(true);
        formikRef?.current?.setFieldValue('reasonForEdit', '');
    }, []);

    const handleDialogClose = useCallback(() => {
        setIsDialogOpen(false);
        formikRef?.current?.setFieldValue('reasonForEdit', '');
    }, []);

    const onFormSubmit = useCallback((values: any, {setSubmitting, setErrors, validateForm}: FormikHelpers<any>) => {
        validateForm();
        const payload = {...values};
        payload.dateOfBirth = commonService.formatDate(payload.dateOfBirth, 'YYYY-MM-DD');
        payload.isInsuranceAvailable = true;
        payload.enbotDetails = enbotDetails;
        // delete payload.isVerified;
        if (payload?.isSameName) {
            payload.firstName = patientDetails?.firstName;
            payload.dateOfBirth = patientDetails?.dateOfBirth;
            payload.middleName = patientDetails?.middleName;
            payload.lastName = patientDetails?.lastName;
        }
        if (payload?.insuranceCompany) {
            payload.payerId = payload?.insuranceCompany?.payerId;
            payload.insuranceCompany = payload?.newInsuranceCompany ? payload?.newInsuranceCompany : payload?.insuranceCompany?.code;
            delete payload.newInsuranceCompany;
        }
        if (payload?.insurancePlan) {
            payload.insurancePlan = payload?.insurancePlan?.code;
        }
        if (payload?.relationship) {
            payload.relationship = payload?.relationship?.code;
        }
        payload.effectiveDate = payload.effectiveDate ? commonService.formatDate(payload.effectiveDate, 'YYYY-MM-DD') : null;
        const formData = CommonService.getFormDataFromJSON(payload);
        setSubmitting(true);
        if (insuranceMode === 'add') {
            CommonService._patient.addPatientInsuranceDetails(patientID, formData)
                .then((response: IAPIResponseType<any>) => {
                    if (onUpdateComplete) {
                        onUpdateComplete(response.data);
                    }
                    setSubmitting(false);
                })
                .catch((error: any) => {
                    CommonService.handleErrors(setErrors, error);
                    setSubmitting(false);
                })
        } else {
            CommonService._patient.editPatientInsuranceDetails(patientID, formInitialValues.id, formData)
                .then((response: IAPIResponseType<any>) => {
                    if (onUpdateComplete) {
                        onUpdateComplete(response.data);
                    }
                    setSubmitting(false);
                    formikRef?.current?.setFieldValue('reasonForEdit', '');
                    CommonService._alert.showToast(response?.message || 'Insurance details has been added', "success");
                })
                .catch((error: any) => {
                    CommonService.handleErrors(setErrors, error);
                    setSubmitting(false);
                })
        }
    }, [onUpdateComplete, patientID, insuranceMode, formInitialValues, patientDetails, enbotDetails]);


    // const handleVerify = useCallback(() => {
    //     setVerified(false);
    //     if (formikRef.current) {
    //         formikRef.current.setFieldValue("isVerified", false);
    //     }
    //     setChecked(false);
    // }, []);

    useEffect(() => {
        if (formInitialValues) {
            if (formInitialValues?.frontImage === null || formInitialValues?.frontImage === undefined || Object.keys(formInitialValues?.frontImage).length === 0) {
                formInitialValues.frontImage = null;
            }
            if (formInitialValues?.backImage === null || formInitialValues?.backImage === undefined || Object.keys(formInitialValues?.backImage).length === 0) {
                formInitialValues.backImage = null;
            }
            const isInsuranceExists = insuranceCompanyList.some((insurance: any) => insurance.code?.toLowerCase() === formInitialValues?.insuranceCompany?.toLowerCase());
            let otherInsuranceCompany = {}
            if (!isInsuranceExists) {
                otherInsuranceCompany = {
                    title: "Other",
                    code: "other"
                }
            }

            const formInitialValuesObj = {
                isSameName: formInitialValues.isSameName,
                firstName: !formInitialValues.isSameName ? formInitialValues?.firstName : "",
                middleName: !formInitialValues.isSameName ? formInitialValues?.middleName : "",
                lastName: !formInitialValues.isSameName ? formInitialValues?.lastName : "",
                dateOfBirth: !formInitialValues.isSameName ? formInitialValues?.dateOfBirth : "",
                isInsuranceAvailable: formInitialValues.isInsuranceAvailable,
                policyNumber: formInitialValues?.policyNumber,
                insuranceCompany: !isInsuranceExists ? otherInsuranceCompany : formInitialValues?.insuranceCompanyObj,
                newInsuranceCompany: !isInsuranceExists ? formInitialValues?.insuranceCompanyObj?.title : "",
                insurancePlan: formInitialValues?.insurancePlanObj,
                relationship: formInitialValues?.relationshipObj,
                // isVerified: formInitialValues?.enbotDetails?.isVerified,
                isVerified: false,
                frontImage: formInitialValues?.frontImage,
                backImage: formInitialValues?.backImage,
                payerId: formInitialValues?.payerId ? formInitialValues?.payerId : null,
                isChecked: insuranceMode === 'edit' ? true : false,
                networkPlanName: formInitialValues.networkPlanName || null,
                groupName: formInitialValues.groupName || null,
                groupNetwork: formInitialValues.groupNetwork || null,
                effectiveDate: formInitialValues.effectiveDate || null,
                ipaMedicalGroupName: formInitialValues.ipaMedicalGroupName || null,
                reasonForEdit: '',
                //field added as per new clearing house changes
                clearingHouse: formInitialValues?.clearingHouse || '',
                clearingHouseId: formInitialValues?.clearingHouseId || '',
                CPID: formInitialValues.CPID || '',
                groupId: formInitialValues?.groupId || '',
                type: formInitialValues?.type || '',
            };
            // if (formInitialValues.isInsuranceAvailable && formInitialValues?.policyNumber &&
            //     formInitialValues?.insuranceCompanyObj && formInitialValues?.relationshipObj) {
            //     formInitialValuesObj.isVerified = true;
            //     console.log(formInitialValuesObj.isVerified);
            //     if (formikRef.current) {
            //         formikRef.current.setFieldValue("isVerified", true);
            //     }
            // }
            setInsuranceInitialValues(formInitialValuesObj);
        }
    }, [formInitialValues, insuranceCompanyList, insuranceMode]);

    // useEffect(() => {
    //     if (insuranceMode === 'edit') {
    //         // setVerified(true);
    //         setChecked(true);
    //     }
    // }, [insuranceMode])

    return (
        <div className="insurance-form-screen screen t-form-screen">
            <div className="insurance-form-header">
                <HeadingComponent title={insuranceMode === 'add' ? "Add Insurance" : "Edit Insurance"}/>
                {/*{*/}
                {/*    <div className="deselect-content" onClick={() => handleClearAll()}>*/}
                {/*        Clear All*/}
                {/*    </div>*/}
                {/*}*/}
            </div>
            <div className="t-form">
                <Formik
                    validationSchema={insuranceValidationSchema(isInsuranceImageRequired)}
                    initialValues={insuranceInitialValues}
                    enableReinitialize={true}
                    onSubmit={onFormSubmit}
                    innerRef={formikRef}
                >
                    {({isSubmitting, values, isValid, touched, setFieldValue, validateForm, errors, submitForm}) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                            console.log(values, 'this is the values')
                        }, [validateForm, values]);
                        return (
                            <Form noValidate={true} className={"insurance-form-holder"}>
                                <PatientEditConfirmationComponent
                                    isOpen={isDialogOpen}
                                    onSubmit={submitForm}
                                    disabledNext={!values?.reasonForEdit}
                                    isSubmitting={isSubmitting}
                                    onClose={handleDialogClose}
                                />
                                <div className="t-form-controls-wrapper">
                                    <div className={'d-flex align-items-center mrg-bottom-20'}>
                                        <div className=''>
                                            Same as patient name?
                                        </div>
                                        <div className={'mrg-left-20'}>
                                            <Field name={'isSameName'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikRadioButtonGroupComponent
                                                            direction={"row"}
                                                            formikField={field}
                                                            titleKey={'title'}
                                                            valueKey={'code'}
                                                            isValueBoolean={true}
                                                            // onUpdate={(value: any) => {
                                                            //     handleVerify()
                                                            // }}
                                                            options={CommonService._staticData.yesNoOptions}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    {
                                        !values.isSameName &&
                                        <>
                                            <Field name={'firstName'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'First Name'}
                                                            placeholder={'Enter First Name'}
                                                            type={"text"}
                                                            required={true}
                                                            // onUpdate={(value: any) => {
                                                            //     handleVerify()
                                                            // }}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <Field name={'middleName'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Middle Name'}
                                                            placeholder={'Enter Middle Name'}
                                                            type={"text"}
                                                            // onUpdate={(value: any) => {
                                                            //     handleVerify()
                                                            // }}
                                                            required={false}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <Field name={'lastName'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Last Name'}
                                                            placeholder={'Enter Last Name'}
                                                            type={"text"}
                                                            // onUpdate={(value: any) => {
                                                            //     handleVerify()
                                                            // }}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <Field name={'dateOfBirth'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikDatePickerComponent
                                                            label={'Date of Birth'}
                                                            formikField={field}
                                                            required={true}
                                                            // onUpdate={(value: any) => {
                                                            //     handleVerify()
                                                            // }}
                                                            maxDate={formValidationRules.today}
                                                            placeholder={'Enter Date of Birth'}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </>
                                    }
                                    <Field name={'insuranceCompany'}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikAutoCompleteDropdown
                                                    label={'Insurance Company'}
                                                    required={true}
                                                    formikField={field}
                                                    // readOnly={verified && insuranceMode === 'add'}
                                                    data={insuranceCompanyList}
                                                    placeholder={"Search Insurance Company"}
                                                    valueKey={"code"}
                                                    renderOption={(props, option: any) => (
                                                        <li {...props}>
                                                            <div>
                                                                <strong>{option.title}</strong>
                                                                <div>{`Payer Id: ${option?.payerId || 'No Payer ID'}`}</div>
                                                            </div>
                                                        </li>
                                                    )}
                                                    // defaultData={[
                                                    //     {
                                                    //         title: "Other",
                                                    //         code: "other"
                                                    //     }
                                                    // ]}
                                                    onUpdate={(value) => {
                                                        setFieldValue('clearingHouse', value.clearingHouse || '')
                                                        setFieldValue('clearingHouseCode', value.clearingHouseCode || '')
                                                        setFieldValue('clearingHouseId', value.clearingHouseId || '')
                                                        setFieldValue('CPID', value.CPID || '')
                                                        setFieldValue('groupId', value.groupId || '')
                                                        setFieldValue('type', value.type?.toLowerCase() || '')
                                                        if (value?.code !== "other") {
                                                            setFieldValue("newInsuranceCompany", '')
                                                        }
                                                    }}
                                                    getOptionLabel={(option => (option?.title + " " + option?.payerId + " " + option?.id) || '')}
                                                />
                                            )
                                        }
                                    </Field>
                                    {values?.insuranceCompany?.code === 'other' &&
                                        <Field name={'newInsuranceCompany'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={'New Insurance Company'}
                                                        placeholder={'Enter New Insurance Company'}
                                                        required={true}
                                                        // onUpdate={(value: any) => {
                                                        //     handleVerify()
                                                        // }}
                                                        multiline={false}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    }

                                    <Field name={'relationship'}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikAutoCompleteDropdown
                                                    label={'Relationship'}
                                                    required={true}
                                                    formikField={field}
                                                    // onUpdate={(value: any) => {
                                                    //     handleVerify()
                                                    // }}
                                                    // readOnly={verified && insuranceMode === 'add'}
                                                    // isDataLoading={isRelationshipListLoading}
                                                    // isDataLoadingFailed={isRelationshipListLoadingFailed}
                                                    data={relationShipList}
                                                    valueKey={"code"}
                                                    placeholder={"Select Relationship*"}
                                                    getOptionLabel={(option => (option.title) || '')}
                                                />
                                            )
                                        }
                                    </Field>
                                    <Field name={'policyNumber'}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikInputComponent
                                                    label={'Policy Number'}
                                                    placeholder={'Policy Number*'}
                                                    // onUpdate={(value: any) => {
                                                    //     handleVerify()
                                                    // }}
                                                    // readOnly={verified && insuranceMode === 'add'}
                                                    required={true}
                                                    multiline={false}
                                                    formikField={field}
                                                />
                                            )
                                        }
                                    </Field>
                                    <Field name={"networkPlanName"}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikInputComponent
                                                    label={'Network/Plan Name'}
                                                    placeholder={'Enter network/plan name'}
                                                    // onUpdate={(value: any) => {
                                                    //     handleVerify()
                                                    // }}
                                                    required={true}
                                                    multiline={false}
                                                    formikField={field}
                                                />
                                            )
                                        }
                                    </Field>
                                    <Field name={"groupName"}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikInputComponent
                                                    label={'Group Name'}
                                                    placeholder={'Enter group name'}
                                                    // onUpdate={(value: any) => {
                                                    //     handleVerify()
                                                    // }}
                                                    required={false}
                                                    multiline={false}
                                                    formikField={field}
                                                />
                                            )
                                        }
                                    </Field>
                                    <Field name={"groupNetwork"}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikInputComponent
                                                    label={'Group/Network'}
                                                    placeholder={'Enter group/network'}
                                                    // onUpdate={(value: any) => {
                                                    //     handleVerify()
                                                    // }}
                                                    required={false}
                                                    multiline={false}
                                                    formikField={field}
                                                />
                                            )
                                        }
                                    </Field>
                                    <Field name={"effectiveDate"}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikDatePickerComponent
                                                    label={'Effective Date'}
                                                    formikField={field}
                                                    // onUpdate={(value: any) => {
                                                    //     handleVerify()
                                                    // }}
                                                    placeholder={'Select Effective Date'}
                                                    minDate={formValidationRules.today}
                                                />
                                            )
                                        }
                                    </Field>
                                    <Field name={"ipaMedicalGroupName"}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikInputComponent
                                                    label={'IPA/Medical Group Name'}
                                                    placeholder={'Enter IPA/Medical Group Name'}
                                                    // onUpdate={(value: any) => {
                                                    //     handleVerify()
                                                    // }}
                                                    required={false}
                                                    multiline={false}
                                                    formikField={field}
                                                />
                                            )
                                        }
                                    </Field>
                                    {/*field added as per new clearing house changes*/}
                                    <Field name={"clearingHouse"}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikInputComponent
                                                    label={'Clearing House'}
                                                    placeholder={'Enter Clearing House Name'}
                                                    required={values?.insuranceCompany?.code === 'other'}
                                                    multiline={false}
                                                    formikField={field}
                                                    disabled={values?.insuranceCompany?.code !== 'other'}
                                                />
                                            )
                                        }
                                    </Field>
                                    <Field name={"CPID"}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikInputComponent
                                                    label={'CPID'}
                                                    placeholder={'Enter CPID'}
                                                    required={values?.insuranceCompany?.code === 'other'}
                                                    multiline={false}
                                                    formikField={field}
                                                    disabled={values?.insuranceCompany?.code !== 'other'}
                                                />
                                            )
                                        }
                                    </Field>
                                    <Field name={"type"}>
                                        {
                                            (field: FieldProps) => (
                                                <FormikSelectDropdownComponent
                                                    label={'Insurance Type'}
                                                    options={CommonService._staticData.insuranceTypes}
                                                    placeholder={'Select Insurance Type'}
                                                    valueKey={'code'}
                                                    required={values?.insuranceCompany?.code === 'other'}
                                                    getOptionLabel={(option) => (option.title) || ''}
                                                    formikField={field}
                                                    disabled={values?.insuranceCompany?.code !== 'other'}
                                                    readOnly={values?.insuranceCompany?.code !== 'other'}
                                                />
                                            )
                                        }
                                    </Field>
                                    {/*{*/}
                                    {/*    enbotError ?*/}
                                    {/*        <div className="enbot-unavailable-message">*/}
                                    {/*            <div*/}
                                    {/*                className="enbot-unavailable-message-text">{enbotErrorMessage}</div>*/}
                                    {/*            <div>*/}
                                    {/*                <ButtonComponent*/}
                                    {/*                    variant={"contained"}*/}
                                    {/*                    color={'secondary'}*/}
                                    {/*                    className={'mrg-top-10'}*/}
                                    {/*                    fullWidth={true}*/}
                                    {/*                    isLoading={isInsuranceVerifying}*/}
                                    {/*                    disabled={!isValid}*/}
                                    {/*                    handleClick={() => verifyInsuranceDetails(values, setFieldValue)}*/}
                                    {/*                >*/}
                                    {/*                    Re Check*/}
                                    {/*                </ButtonComponent>*/}
                                    {/*            </div>*/}
                                    {/*        </div> :*/}
                                    {/*        <div className='verify-insurance-wrapper'>*/}
                                    {/*            {checked === false ?*/}
                                    {/*                <div className='verify-insurance-button'>*/}
                                    {/*                    <ButtonComponent*/}
                                    {/*                        variant={"contained"}*/}
                                    {/*                        color={'secondary'}*/}
                                    {/*                        fullWidth={true}*/}
                                    {/*                        isLoading={isInsuranceVerifying}*/}
                                    {/*                        disabled={!isValid}*/}
                                    {/*                        handleClick={() => verifyInsuranceDetails(values, setFieldValue)}*/}
                                    {/*                    >*/}
                                    {/*                        Verify Insurance*/}
                                    {/*                    </ButtonComponent>*/}
                                    {/*                </div>*/}
                                    {/*                :*/}
                                    {/*                verified ?*/}
                                    {/*                    <div className='verify-insurance-icon'>*/}
                                    {/*                        <ChipComponent*/}
                                    {/*                            icon={<CheckCircleIcon/>}*/}
                                    {/*                            size="medium" label="Verified"/>*/}
                                    {/*                    </div> :*/}
                                    {/*                    <div className='verify-insurance-icon'>*/}
                                    {/*                        <ChipComponent*/}
                                    {/*                            icon={<CancelIcon/>}*/}
                                    {/*                            size="medium" label="Not Verified"/>*/}
                                    {/*                    </div>*/}
                                    {/*            }*/}
                                    {/*        </div>*/}
                                    {/*}*/}

                                    <div className="insurance-attachment-upload-wrapper">
                                        <div className="insurance-attachment-upload-item">
                                            <div className="insurance-card-image-title-wrapper">
                                                <div className="insurance-card-image-title">
                                                    Insurance Card Front
                                                    {
                                                        isInsuranceImageRequired &&
                                                        <span className={'required-asterisk'}>*</span>
                                                    }
                                                </div>
                                                <div className="mrg-left-15">
                                                    <TooltipComponent
                                                        placement={'top'}
                                                        tooltip={"Only JPEG and PNG  files with max. size of 100 MB are allowed."}
                                                        arrow={true}>
                                                        <HelpIcon/>
                                                    </TooltipComponent>
                                                </div>
                                            </div>

                                            {!values.frontImage ? <>
                                                    <input type={"file"}
                                                           className={"display-none"}
                                                           ref={insuranceFrontFileRef}
                                                           accept={"image/jpg, image/jpeg, image/png"}
                                                           onChange={(event) => {
                                                               handleInsuranceAttachmentUpload("frontImage", event);
                                                           }}
                                                    />
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} sm={6} md={6}>
                                                            <ButtonComponent
                                                                variant={"outlined"}
                                                                fullWidth={true}
                                                                color={"secondary"}
                                                                disabled={!checked}
                                                                handleClick={() => {
                                                                    insuranceFrontFileRef.current?.click()
                                                                }}
                                                                type={'button'}>
                                                                Upload
                                                            </ButtonComponent>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6} md={6}>
                                                        </Grid>
                                                    </Grid>
                                                    {
                                                        (_.get(touched, "frontImage") && !!(_.get(errors, "frontImage"))) &&
                                                        <ErrorComponent
                                                            errorText={(_.get(errors, "frontImage"))}/>
                                                    }
                                                </>
                                                : <>
                                                    <ChipComponent
                                                        label={'Insurance Card Front'}
                                                        onClick={() => {
                                                            handleAttachmentPreviewOpen("frontImage");
                                                        }}
                                                        onDelete={() => {
                                                            setFieldValue("frontImage", null);
                                                        }}
                                                        icon={<AttachFile/>}
                                                    />
                                                </>
                                            }
                                        </div>
                                        <div className="insurance-attachment-upload-item">
                                            <div className="insurance-card-image-title-wrapper">
                                                <div className="insurance-card-image-title">
                                                    Insurance Card Back
                                                    {
                                                        isInsuranceImageRequired &&
                                                        <span className={'required-asterisk'}>*</span>
                                                    }
                                                </div>
                                                <div className="mrg-left-15">
                                                    <TooltipComponent
                                                        placement={'top'}
                                                        tooltip={"Only JPEG and PNG files with max. size of 100 MB are allowed."}
                                                        arrow={true}>
                                                        <HelpIcon/>
                                                    </TooltipComponent>
                                                </div>
                                            </div>

                                            {!values.backImage ? <>
                                                    <input type={"file"}
                                                           className={"display-none"}
                                                           ref={insuranceBackFileRef}
                                                           accept={"image/jpg, image/jpeg, image/png"}
                                                           onChange={(event) => {
                                                               handleInsuranceAttachmentUpload("backImage", event);
                                                           }}
                                                    />
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} sm={6} md={6}>
                                                            <ButtonComponent
                                                                variant={"outlined"}
                                                                fullWidth={true}
                                                                color={'secondary'}
                                                                disabled={!checked}
                                                                handleClick={() => {
                                                                    insuranceBackFileRef.current?.click()
                                                                }}
                                                                type={'button'}>
                                                                Upload
                                                            </ButtonComponent>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6} md={6}>
                                                        </Grid>
                                                    </Grid>
                                                    {
                                                        (_.get(touched, "backImage") && !!(_.get(errors, "backImage"))) &&
                                                        <ErrorComponent
                                                            errorText={(_.get(errors, "backImage"))}/>
                                                    }
                                                </> :
                                                <>
                                                    <ChipComponent
                                                        label={'Insurance Card Back'}
                                                        onClick={() => {
                                                            handleAttachmentPreviewOpen("backImage");
                                                        }}
                                                        onDelete={() => {
                                                            setFieldValue("backImage", null);
                                                        }}
                                                        icon={<AttachFile/>}
                                                    />
                                                    {
                                                        (_.get(touched, "backImage") && !!(_.get(errors, "backImage"))) &&
                                                        <ErrorComponent
                                                            errorText={(_.get(errors, "insuranceCopyBack"))}/>
                                                    }
                                                </>
                                            }
                                        </div>
                                    </div>
                                </div>
                                <div className="drawer-actions form-action-section mrg-top-20">
                                    <ButtonComponent variant={"contained"}
                                                     fullWidth={true}
                                                     type={insuranceMode === 'add' ? 'submit' : undefined}
                                                     isLoading={isSubmitting}
                                                     disabled={!(isValid && checked)}
                                                     handleClick={insuranceMode === 'edit' ? handleDialogOpen : undefined}
                                                     color={"primary"}>
                                        Save
                                    </ButtonComponent>
                                </div>
                            </Form>
                        )
                    }}
                </Formik>
            </div>
            <DialogComponent open={showFrontAttachmentPreviewDialog}
                             maxWidth={'md'}
                             onClose={() => {
                                 setShowFrontAttachmentPreviewDialog(false)
                             }}
                             isShowCancel={true}
                             title={'Insurance Card Front'}
                             className={"attachment-preview-dialog"}>

                <div className="attachment-preview-dialog-body" id="file_preview_component">
                    <FilePreviewerComponent
                        attachmentFile={currentFrontAttachmentFile}
                        attachmentUrl={currentFrontAttachmentFile}
                        isFile={isNewFrontInsuranceImage}/>
                </div>
                {currentFrontAttachmentFile &&
                    <div className="uploaded-attachment-file-name">
                        {!isFrontCancelVisible ? <>
                                <span className="font-weight-bold"> Uploaded File Name:&nbsp;</span>
                                <span>{currentFrontAttachmentFile?.name}</span>
                            </> :
                            <></>
                        }
                    </div>
                }
                <div className="attachment-preview-dialog-footer">
                    {
                        isFrontCancelVisible ?
                            <ButtonComponent variant={"outlined"}
                                             id="cancel_btn"
                                             fullWidth={true}
                                             handleClick={() => {
                                                 handleFrontAttachmentDialogClose();
                                             }}
                            >
                                Cancel
                            </ButtonComponent> :
                            <>
                                <ButtonComponent variant={"outlined"}
                                                 id="re_upload_btn"
                                                 fullWidth={true}
                                                 handleClick={() => {
                                                     insuranceFrontFileRef.current?.click()
                                                 }}
                                >
                                    Re-upload
                                </ButtonComponent>&nbsp;

                                <ButtonComponent variant={"contained"}
                                                 id="continue_btn"
                                                 fullWidth={true}
                                                 handleClick={handleFrontAttachmentDialogContinue}>
                                    Confirm Upload
                                </ButtonComponent>
                            </>}
                </div>
            </DialogComponent>
            <DialogComponent open={showBackAttachmentPreviewDialog}
                             maxWidth={'md'}
                             onClose={() => {
                                 handleBackAttachmentDialogClose()
                             }}
                             isShowCancel={true}
                             title={'Insurance Card Back'}
                             className={"attachment-preview-dialog"}>

                <div className="attachment-preview-dialog-body" id="file_preview_component">
                    <FilePreviewerComponent
                        attachmentFile={currentBackAttachmentFile}
                        attachmentUrl={currentBackAttachmentFile}
                        isFile={isNewBackInsuranceImage}/>
                </div>
                {currentBackAttachmentFile &&
                    <div className="uploaded-attachment-file-name">
                        {
                            !isBackCancelVisible ? <>
                                    <span className="font-weight-bold"> Uploaded File Name:&nbsp;</span>
                                    <span>{currentBackAttachmentFile?.name}</span>
                                </> :
                                <></>
                        }
                    </div>
                }
                <div className="attachment-preview-dialog-footer">
                    {
                        isBackCancelVisible ?
                            <ButtonComponent variant={"outlined"}
                                             id="cancel_btn"
                                             fullWidth={true}
                                             handleClick={() => {
                                                 setShowBackAttachmentPreviewDialog(false)
                                             }}
                            >
                                Cancel
                            </ButtonComponent> :
                            <>
                                <ButtonComponent variant={"outlined"}
                                                 id="re_upload_btn"
                                                 fullWidth={true}
                                                 handleClick={() => {
                                                     insuranceBackFileRef.current?.click()
                                                 }}
                                >
                                    Re-upload
                                </ButtonComponent>&nbsp;
                                <ButtonComponent variant={"contained"}
                                                 id="continue_btn"
                                                 fullWidth={true}
                                                 handleClick={handleBackAttachmentDialogContinue}>
                                    Confirm Upload
                                </ButtonComponent>
                            </>
                    }

                </div>
            </DialogComponent>
        </div>
    )
};

export default InsuranceFormComponent;
